import { useEffect, useMemo, useState } from 'react'
import { ClipboardCheck, CalendarClock, Inbox, FileCheck2 } from 'lucide-react'
import { listAssessments } from '~/api/assessments'
import { getTimeseries } from '~/api/stats'
import { useStats } from '~/api/use-stats'
import type { AdminAssessment, TimeseriesPoint } from '~/api/types'
import {
    ChartCard,
    DonutChart,
    CategoryBarChart,
    SeriesArea,
    KRDS,
    statusColor,
    type DonutDatum,
    type CategoryDatum,
} from '~/components/charts'
import { AssessmentDetailPanel } from '~/components/AssessmentDetailPanel'
import { AssessmentTable, STATUS_TONES } from '~/components/AssessmentTable'
import { FeedbackPanel } from '~/components/FeedbackPanel'
import { ServicePageShell } from '~/components/ServicePageShell'
import { StatCard } from '~/components/StatCard'
import { Badge } from '~/components/ui/badge'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { cn, formatDateTime, whoLabel } from '~/lib/utils'

// 인증평가 서비스 페이지. 개요(KPI·상태/합격가능성 분포·추이·최근 평가) + 데이터 + 피드백 탭.
const TREND_DAYS = 30
const RECENT_LIMIT = 5
// 합격가능성 분포는 별도 API가 없어 목록을 크게 받아 클라이언트에서 집계한다.
const SAMPLE_LIMIT = 200

const STATUS_LABEL: Record<string, string> = {
    collecting: '수집 중',
    scored: '채점 완료',
    reported: '보고서 발행',
}

// 합격가능성 등급 → 라벨·의미색. 알 수 없는 값은 제외(분포 명료성).
const LIKELIHOOD = [
    { key: 'high', label: '높음', color: KRDS.success },
    { key: 'medium', label: '보통', color: KRDS.warning },
    { key: 'low', label: '낮음', color: KRDS.danger },
] as const

export function CertificationPage() {
    const toast = useToast()
    const { stats, error } = useStats()
    const c = stats?.certification

    const [selected, setSelected] = useState<AdminAssessment | null>(null)
    const [recent, setRecent] = useState<AdminAssessment[]>([])
    const [sample, setSample] = useState<AdminAssessment[]>([])
    const [sampleLoading, setSampleLoading] = useState(true)
    const [series, setSeries] = useState<TimeseriesPoint[]>([])

    // 통계 조회 오류는 토스트로 알린다.
    useEffect(() => {
        if (error) toast.error(error)
    }, [error, toast])

    useEffect(() => {
        let alive = true
        listAssessments({ limit: SAMPLE_LIMIT })
            .then((d) => alive && setSample(d))
            .catch(() => {})
            .finally(() => alive && setSampleLoading(false))
        getTimeseries(TREND_DAYS)
            .then((d) => alive && setSeries(d.series))
            .catch(() => {})
        return () => {
            alive = false
        }
    }, [])

    // 최근 5건은 표본의 앞부분(목록은 최신순 가정)을 그대로 쓴다.
    useEffect(() => {
        setRecent(sample.slice(0, RECENT_LIMIT))
    }, [sample])

    // 상태 분포(도넛) — 통계 스냅샷 사용.
    const statusDist: DonutDatum[] = c
        ? [
              {
                  name: STATUS_LABEL.collecting,
                  value: c.collecting,
                  color: statusColor('collecting'),
              },
              {
                  name: STATUS_LABEL.scored,
                  value: c.scored,
                  color: statusColor('scored'),
              },
              {
                  name: STATUS_LABEL.reported,
                  value: c.reported,
                  color: statusColor('reported'),
              },
          ]
        : []
    const statusEmpty = !c || c.total === 0

    // 합격가능성 분포(막대) — 표본 클라이언트 집계.
    const likelihoodData: CategoryDatum[] = useMemo(() => {
        const counts: Record<string, number> = {}
        for (const a of sample) {
            const k = a.passLikelihood?.toLowerCase()
            if (k) counts[k] = (counts[k] ?? 0) + 1
        }
        return LIKELIHOOD.map((l) => ({
            name: l.label,
            value: counts[l.key] ?? 0,
            color: l.color,
        }))
    }, [sample])
    const likelihoodEmpty = likelihoodData.every((d) => d.value === 0)

    const trendEmpty = series.every((p) => p.assessments === 0)

    function openDetail(assessment: AdminAssessment) {
        setSelected(assessment)
    }

    const overview = (
        <div className='space-y-6'>
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-5'>
                <StatCard
                    label='평가'
                    value={c?.total ?? '-'}
                    icon={ClipboardCheck}
                    tone='primary'
                />
                <StatCard
                    label='최근 7일'
                    value={c?.total7d ?? '-'}
                    hint='평가 생성'
                    icon={CalendarClock}
                    tone='info'
                />
                <StatCard
                    label='수집 중'
                    value={c?.collecting ?? '-'}
                    icon={Inbox}
                    tone='info'
                />
                <StatCard label='채점 완료' value={c?.scored ?? '-'} />
                <StatCard
                    label='보고서 발행'
                    value={c?.reported ?? '-'}
                    icon={FileCheck2}
                    tone='success'
                />
            </div>

            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                <ChartCard
                    title='상태 분포'
                    description='수집 → 채점 → 보고서 발행'
                    empty={statusEmpty}
                    emptyText='평가 데이터가 없어요.'>
                    <DonutChart
                        data={statusDist}
                        centerValue={c?.total ?? 0}
                        centerLabel='전체 평가'
                    />
                </ChartCard>

                <ChartCard
                    title='합격가능성 분포'
                    description='채점 완료 평가의 합격 가능성 등급'
                    empty={likelihoodEmpty}
                    emptyText='합격가능성 데이터가 없어요.'>
                    <CategoryBarChart data={likelihoodData} layout='vertical' />
                </ChartCard>
            </div>

            <ChartCard
                title='일별 평가 추이'
                description={`최근 ${TREND_DAYS}일 평가 건수`}
                empty={trendEmpty}
                emptyText='기간 내 평가 데이터가 없어요.'>
                <SeriesArea
                    data={series}
                    dataKey='assessments'
                    name='인증평가'
                    color={KRDS.info}
                />
            </ChartCard>

            <Card>
                <div className='p-5 pb-3'>
                    <h3 className='krds-title text-foreground'>최근 평가</h3>
                    <p className='text-muted-foreground mt-0.5 text-sm'>
                        행을 클릭하면 평가 상세를 봐요.
                    </p>
                </div>
                <div className='px-5 pb-5'>
                    {!sampleLoading && recent.length === 0 ? (
                        <EmptyState
                            icon={ClipboardCheck}
                            title='평가가 없어요.'
                            description='인증 평가가 생성되면 여기에 표시돼요.'
                        />
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>제목</TableHead>
                                    <TableHead>규모</TableHead>
                                    <TableHead>사용자</TableHead>
                                    <TableHead>상태</TableHead>
                                    <TableHead>점수</TableHead>
                                    <TableHead>생성일</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sampleLoading
                                    ? Array.from({ length: RECENT_LIMIT }).map(
                                          (_, i) => (
                                              <TableRow key={i}>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-40' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-16' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-28' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-16' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-16' />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton className='h-4 w-28' />
                                                  </TableCell>
                                              </TableRow>
                                          )
                                      )
                                    : recent.map((a) => (
                                          <TableRow
                                              key={a.id}
                                              onClick={() => openDetail(a)}
                                              className={cn(
                                                  'cursor-pointer',
                                                  a.id === selected?.id &&
                                                      'bg-accent hover:bg-accent'
                                              )}>
                                              <TableCell className='font-bold'>
                                                  {a.title}
                                              </TableCell>
                                              <TableCell>
                                                  {a.scale ?? '-'}
                                              </TableCell>
                                              <TableCell
                                                  className='max-w-[180px] truncate'
                                                  title={
                                                      a.ownerEmail ?? undefined
                                                  }>
                                                  {whoLabel(
                                                      a.ownerName,
                                                      a.ownerEmail
                                                  )}
                                              </TableCell>
                                              <TableCell>
                                                  <Badge
                                                      tone={
                                                          STATUS_TONES[
                                                              a.status
                                                          ] ?? 'neutral'
                                                      }>
                                                      {a.status}
                                                  </Badge>
                                              </TableCell>
                                              <TableCell className='whitespace-nowrap'>
                                                  {a.totalScore ?? '-'}
                                                  {a.maxScore != null
                                                      ? ` / ${a.maxScore}`
                                                      : ''}
                                              </TableCell>
                                              <TableCell>
                                                  {formatDateTime(a.createdAt)}
                                              </TableCell>
                                          </TableRow>
                                      ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </Card>
        </div>
    )

    const data = (
        <AssessmentTable onSelect={setSelected} selectedId={selected?.id} />
    )

    return (
        <>
            <ServicePageShell
                title='인증평가'
                description='인증 평가 서비스 개요'
                tabs={[
                    { label: '개요', content: overview },
                    { label: '데이터', content: data },
                    {
                        label: '피드백',
                        content: <FeedbackPanel service='certification' />,
                    },
                ]}
            />
            {selected ? (
                <AssessmentDetailPanel
                    assessmentId={selected.id}
                    onClose={() => setSelected(null)}
                />
            ) : null}
        </>
    )
}
